<template>
  <div class="date-slider w-full relative px-[2.4rem] py-[0.6rem]">
    <button
      v-if="!disablePrev"
      type="button"
      :aria-label="$t('Previous')"
      class="date-slider-prev"
      :disabled="loading || disablePrev"
      @click="$emit('prev')"
    >
      <InlineSpinner
        v-if="loading"
        size="18"
        class="mx-auto"
      />
      <template v-else>
        <img :src="dateSliderLeftIcon" alt="" aria-hidden="true" class="date-slider-icon" />
      </template>
    </button>

    <div class="overflow-hidden w-full">
      <div
        class="days-container w-full"
        :style="{
          display: 'grid',
          gridTemplateColumns: `repeat(${dates.length}, 1fr)`,
          gap: '0.6rem',
        }"
      >
        <button
          v-for="(date, index) in dates"
          :key="index"
          class="date-slider-day text-center"
          :class="[{ 'activeDate': activeIndex === index }, { 'date-slider-day--compact': compact }]"
          :disabled="loading"
          @click="$emit('select', index)"
        >
          <template v-if="compact">
            <span class="date-slider-weekday">{{ date.weekday }}</span>
            <span class="date-slider-date">{{ date.day }} {{ date.month }}</span>
          </template>
          <template v-else>
            <span class="date-slider-month">{{ date.month }}</span>
            <span class="date-slider-day-number">{{ date.day }}</span>
            <span class="date-slider-weekday">{{ date.weekday }}</span>
          </template>
        </button>
      </div>
    </div>

    <button
      type="button"
      :aria-label="$t('Next')"
      class="date-slider-next"
      :disabled="loading || disableNext"
      @click="$emit('next')"
    >
      <InlineSpinner
        v-if="loading"
        size="18"
        class="mx-auto"
      />
      <template v-else>
        <img :src="dateSliderRightIcon" alt="" aria-hidden="true" class="date-slider-icon" />
      </template>
    </button>
  </div>
</template>

<script>
import InlineSpinner from '@/components/loader/InlineSpinner.vue';
import DateSliderLeftIcon from '@/assets/images/date-slider-left.svg';
import DateSliderRightIcon from '@/assets/images/date-slider-right.svg';

export default {
  name: 'DoctorScheduleDateSlider',
  components: {
    InlineSpinner,
  },
  props: {
    dates: {
      type: Array,
      default: () => [],
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disablePrev: {
      type: Boolean,
      default: false,
    },
    disableNext: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dateSliderLeftIcon: DateSliderLeftIcon,
      dateSliderRightIcon: DateSliderRightIcon,
    };
  },
};
</script>

<style scoped>
.days-container {
  @apply items-center w-full select-none my-auto;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.date-slider-prev {
  @apply left-2;
}
.date-slider-next {
  @apply right-2;
}
.date-slider-prev:before,
.date-slider-next:before {
  @apply text-[5rem] leading-loose text-[#0079d2];
}
.date-slider-prev,
.date-slider-next {
  @apply text-[0] leading-[0] absolute block translate-x-0 -translate-y-2/4 cursor-pointer text-transparent p-0 border-[none] top-2/4;
  -webkit-transform: translate(0, -50%);
  -ms-transform: translate(0, -50%);
  outline: none;
  background: transparent;
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.date-slider-prev[disabled] {
  @apply cursor-auto;
}
.date-slider-next[disabled] {
  @apply cursor-auto;
}
.date-slider-prev:not([disabled]):hover,
.date-slider-next:not([disabled]):hover {
  transform: translate(0, -50%) scale(1.1);
  opacity: 0.7;
}

/* Day = column header for the times below (not a date picker). Plain label,
   not a button, so it doesn't look selectable. */
.date-slider-day {
  @apply flex justify-center items-center text-center flex-col gap-[0.1rem] text-[#8B8B8B] text-[1.1rem] relative cursor-default border-[none] bg-transparent;
  padding: 0.2rem 0.3rem 0.6rem;
}
.date-slider-day:disabled {
  @apply opacity-60 cursor-default;
}
.date-slider-month,
.date-slider-weekday {
  @apply text-[1rem] text-[#8B8B8B];
}

.date-slider-day--compact {
  @apply gap-2;
}

.date-slider-date {
  @apply text-[1.2rem] text-black font-medium leading-[1.6rem];
}

.date-slider-day-number {
  @apply text-[1.6rem] leading-[1.6rem] font-semibold text-[#2D2244];
}

.date-slider-day[date-slider-selected="true"] .date-slider-month,
.date-slider-day[date-slider-selected="true"] .date-slider-weekday,
.date-slider-day.activeDate .date-slider-month,
.date-slider-day.activeDate .date-slider-weekday {
  @apply text-[#8B8B8B];
}

.date-slider-day[date-slider-selected="true"] .date-slider-day-number,
.date-slider-day.activeDate .date-slider-day-number {
  @apply text-[#2D2244];
}

.date-slider-day.activeDate .date-slider-date {
  @apply text-black;
}

.date-slider-icon {
  @apply w-[13px] h-[13px];
}
</style>
